import { ClientApiFactory } from '@boostbrothers/web-api/web-gateway-api';
import type { ViewerRef } from '@boostbrothers/wysiwyg';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useToast from '~hooks/useToast';
import {
  getTemporaryContent,
  removeTemporaryContent,
  setTemporaryContent,
} from '~utils/editor';
import { Form, Header, Preview } from './components';

import '@boostbrothers/wysiwyg/editor.css';
import '@boostbrothers/wysiwyg/viewer.css';

const PREVIEW_WIDTH = 500;
const EDITOR_WIDTH = 688;

const Edit = () => {
  const initializedCompleted = useRef(false);
  const viewerRef = useRef<ViewerRef>(null);

  const [document, setDocument] = useState('');
  const [title, setTitle] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [thumbnail, setThumbnail] = useState({
    url: '',
    fileName: '',
  });

  const [searchParams] = useSearchParams();
  const contentId = searchParams.get('contentId');
  const { addToast } = useToast();

  const { data: savedContent } = useQuery({
    queryKey: ['content', contentId],
    queryFn: contentId
      ? () => {
          return ClientApiFactory('GET /v1/cms/contents/:contentId')({
            params: { contentId: contentId },
          });
        }
      : skipToken,
  });

  const { data: userInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: contentId
      ? skipToken
      : () => ClientApiFactory('GET /v1/cms/editor-users')(),
    staleTime: Infinity,
  });

  /**
   * @description sessionStorage에 userToken 유무를 통해 PA에서 진입했는지 여부를 확인합니다.
   */
  const fromPA = useMemo(() => {
    return !!sessionStorage.getItem('userToken');
  }, []);

  const authorNickname = useMemo(() => {
    return savedContent?.author ?? userInfo?.nickname ?? '';
  }, [savedContent, userInfo]);

  /**
   * @description 글 수정 시, 기존 작성된 글 내용을 동기화합니다.
   * 1. 세션 스토리지에 임시 롤백된 데이터가 있으면 그 데이터를 사용합니다.
   * 2. 서버에서 가져온 기존 데이터가 있으면 그 데이터를 사용합니다.
   * 3. 아무것도 없으면 빈 상태로 둡니다.
   */
  useEffect(() => {
    const temporaryContent = getTemporaryContent();

    const overwriteContent = temporaryContent ? temporaryContent : savedContent;

    if (initializedCompleted.current) return;

    if (overwriteContent) {
      setTitle(overwriteContent.title);
      setDocument(overwriteContent.content);
      setPreviewText(overwriteContent.previewText || '');
      setThumbnail({
        url: overwriteContent.thumbnailUrl || '',
        fileName: overwriteContent.thumbnailFileName || '',
      });

      initializedCompleted.current = true;
    }

    if (temporaryContent) {
      removeTemporaryContent();
      addToast({
        title: '작성하시던 내용을 그대로 불러왔어요.',
        state: 'success',
        position: 'bottom-center',
      });
    }
  }, [addToast, savedContent]);

  const handleCursorNodeChange = useCallback((nodeIndex: number) => {
    viewerRef.current?.scrollToNode(nodeIndex);
  }, []);

  const handleTemporarySave = () => {
    setTemporaryContent({
      title,
      content: document,
      previewText,
      thumbnailUrl: thumbnail.url,
      thumbnailFileName: thumbnail.fileName,
      contentId,
    });
  };

  return (
    <section className="max-w-[1200px] min-h-[calc(100vh-24px)] flex items-stretch gap-x-3 my-3 mx-auto">
      <section className="flex flex-col flex-1 gap-3">
        {/* Header */}
        <Header
          contentId={contentId}
          title={title}
          document={document}
          previewText={previewText}
          thumbnail={thumbnail}
          savedContent={savedContent}
          fromPA={fromPA}
          handleTemporarySave={handleTemporarySave}
        />

        {/* Main Content Area */}
        <main className="h-full flex flex-col gap-6 rounded-xl bg-white p-4">
          <Form
            title={title}
            document={document}
            previewText={previewText}
            thumbnail={thumbnail}
            fromPA={fromPA}
            setTitle={setTitle}
            setDocument={setDocument}
            setPreviewText={setPreviewText}
            setThumbnail={setThumbnail}
            onCursorNodeChange={handleCursorNodeChange}
            handleTemporarySave={handleTemporarySave}
          />
        </main>
      </section>

      {/* Preview */}

      <div style={{ width: PREVIEW_WIDTH }}></div>
      <section
        style={{ position: 'fixed', marginLeft: EDITOR_WIDTH + 12 }}
        className="min-h-[calc(100vh-24px)] max-h-[calc(100vh-24px)] flex flex-y-1 items-start overflow-y-auto rounded-xl bg-CoolGray-800 px-[70px] py-6 scrollbar-hide"
      >
        <Preview
          ref={viewerRef}
          title={title}
          document={document}
          previewText={previewText}
          thumbnail={thumbnail}
          authorNickname={authorNickname}
        />
      </section>
    </section>
  );
};

export default Edit;
